import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SettingsService } from './settings.service';
import { Project } from '../models/project';
import { MessageService } from './message.service';
import { BoardState } from '../models/boardstate';
import { ConfigLoaderService } from '../config/configloader.service';
import { Statuscategory } from '../models/statuscategory';
import { Status } from '../models/status';
import { Column } from '../models/column';
import { map } from 'rxjs/operators';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  })
};

/**
 * this class represents access to all rest requests concerning Project data of the PyWall
 * this rest service honors isMock from settingservice
 */
@Injectable()
export class ProjectService {

  private projectBaseURL: string;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private settingsService: SettingsService,
    private configService: ConfigLoaderService) {

    this.projectBaseURL = `${this.configService.settings.pyWallServer.url}projects`;
  }

  /**
   * this returns all available project names
   * @returns {Observable<Project[]>}
   */
  getKeysOfAvailableProjects(): Observable<string[]> {
    this.messageService.sendDebug('ProjectService getKeysOfAvailableProjects called.');
    return this.http.get<string[]>(this.projectBaseURL +'/');
  }

  /**
   * this returns all details to a specific project
   * @param {string} projectName
   * @returns {Observable<Project>}
   */
  // seems like this is never being used !!
  getProjectDetails(projectName: string): Observable<Project> {
    this.messageService.sendDebug(`ProjectService getProjectDetails on ${projectName} called.`);
    const targetURL = this.projectBaseURL + `/${projectName}/`;
    return this.http.get<Project>(targetURL);
  }

  /**
   * Returns the Board-Configuration of a project
   * @param {string} projectKey
   * @returns {Observable<BoardState>}
   */
  getBoardStates(projectKey: string): Observable<BoardState> {
    this.messageService.sendDebug(`ProjectService getBoardConfiguration on ${projectKey} called.`);
    const targetURL = this.projectBaseURL + `/${projectKey}/board_configuration/`;
    return this.http.get<BoardState[]>(targetURL, httpOptions).pipe(map(data => this.mapAPIDataToBoardstateObject(data)));
  }

  protected mapAPIDataToBoardstateObject(data: any): BoardState {
    const columns: Column[] = [];
    data['columns'].forEach(columnEntry => {
      const statuses: Status[] = [];

      columnEntry['statuses'].forEach(statusEntry => {
        const statusCategoryData = statusEntry['statusCategory'];
        const statusCategory = new Statuscategory(
          statusCategoryData['colorName'],
          statusCategoryData['id'],
          statusCategoryData['key']
        );

        const status = new Status(
          statusEntry['description'],
          statusEntry['iconUrl'],
          statusEntry['id'],
          statusEntry['name'],
          statusCategory
        );
        statuses.push(status);
      });
      const column = new Column(columnEntry['name'], statuses);
      columns.push(column);
    });
    return new BoardState(columns, data['type']);
  }

  getProjectByKey(projectKey: string): Observable<Project> {
    this.messageService.sendDebug(`ProjectService getProjectByKey called on project ${projectKey}`);
    return this.http.get<Project>(`${this.projectBaseURL}/${projectKey}/`);
  }

  getProjectById(projectId: number): Observable<Project> {
    this.messageService.sendDebug(`ProjectService getProjectById called on project ${projectId}`);
    return this.http.get<Project>(`${this.projectBaseURL}/widgets/team/projects/${projectId}/`);
  }

  saveProjectOnInitialisation(projectKey: string) {
    if (projectKey !== null) {
      this.messageService.sendDebug(`ProjectService saveProject called on project ${projectKey}`);
      return this.http.post<Project>(`${this.projectBaseURL}/widgets/team/projects/`, {
        'key': projectKey
      }, httpOptions);
    }
  }

  updateProjectDoD(project: Project): Observable<Project> {
    this.messageService.sendDebug(`ProjectService updateProjectDoD called on project ${project.key}`);
    return this.http.put<Project>(`${this.projectBaseURL}/${project.key}/`, project, httpOptions);
  }

  updateProject(project: Project): Observable<Project> {
    this.messageService.sendDebug(`ProjectService updateProject called on project ${project.key}`);
    return this.http.put<Project>(`${this.projectBaseURL}/${project.key}/`, project, httpOptions);
  }

}
